import { API_BASE_URL } from './env';
import { ApiError, NetworkError, type ApiErrorBody } from './errors';
import { log } from './offline/log';
import { reportReachable, reportUnreachable } from './offline/connectivity';
import { tokenStore } from './token-store';

export type Page<T> = {
  data: T[];
  page: { limit: number; offset: number; count: number; hasMore: boolean };
};

export type PageQuery = { limit?: number; offset?: number };

type QueryValue = string | number | boolean | null | undefined | readonly string[];

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Skips the bearer header and the refresh retry — used by the auth routes themselves. */
  anonymous?: boolean;
  /** Extra headers, for the few routes that take one — an idempotency key, say. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Milliseconds before the request is abandoned. See `REQUEST_TIMEOUT`. */
  timeoutMs?: number;
};

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `${API_BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    /*
     * A list is REPEATED, never joined.
     *
     * `?status=PACKED&status=READY_FOR_PICKUP` is the shape the backend's
     * `oneOrMany` reads back as an array. `String(['A','B'])` would instead
     * send `status=A,B`, which arrives as the single literal "A,B", fails the
     * enum and answers 400 — or worse, on a filter that tolerated it, matches
     * nothing while looking like it applied.
     */
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
      continue;
    }
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${url}?${search}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const envelope = body as Partial<ApiErrorBody> | null;
  const error = envelope?.error;
  return new ApiError(
    response.status,
    error?.code ?? `HTTP_${response.status}`,
    error?.message ?? response.statusText ?? 'Request failed',
    error?.details,
  );
}

/**
 * Three outcomes, not two, and the difference is the whole point.
 *
 * `rejected` means the server answered and said no: the session really is over
 * and the tokens are cleared. `unreachable` means nobody answered — the session
 * is probably fine, the line is not, and clearing the tokens would sign a
 * counter out because its wifi blinked.
 */
type RefreshOutcome = 'refreshed' | 'rejected' | 'unreachable';

/*
 * One refresh in flight at a time. Without this, a dashboard that fires six
 * queries at once would burn six refresh tokens on a single expiry — and since
 * the backend treats a re-presented token as theft and revokes the family, the
 * losers of that race would log the user out.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

async function refreshSession(): Promise<RefreshOutcome> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return 'rejected';

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        /*
         * The line that was missing, and the one that froze the till.
         *
         * Every other request in the app is bounded. This one — the request
         * that every other request funnels through on a 401 — was not, and
         * `fetch` on a dead line does not reject, it hangs. So a save that met
         * an expired token stopped here forever: it could not fail, so it could
         * not fall through to the offline queue, and the Save button spun with
         * a customer standing at the counter.
         */
        signal: AbortSignal.timeout(REQUEST_TIMEOUT),
      });
      if (!response.ok) {
        tokenStore.clear();
        return 'rejected';
      }
      const payload = (await response.json()) as {
        data: { accessToken: string; refreshToken: string };
      };
      tokenStore.set(payload.data);
      return 'refreshed';
    } catch {
      // Tokens deliberately left alone. See `RefreshOutcome`.
      return 'unreachable';
    } finally {
      // Cleared in a microtask so every awaiter of this attempt sees one result.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/** Resolves when a signal aborts, so a deadline can be raced against a promise. */
function whenAborted(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    signal.addEventListener('abort', () => resolve(), { once: true });
  });
}

/**
 * How long to wait before deciding nobody is answering.
 *
 * There used to be no limit at all, and on a dead network `fetch` does not
 * reject — it hangs, for minutes, sometimes until the tab is closed. On this
 * app that meant a till whose save button spun forever and then stayed
 * disabled, which is exactly the moment a pharmacy most needs it to work.
 *
 * Twelve seconds is well past any real response on a shop's connection and well
 * short of a counter deciding the software is broken.
 */
const REQUEST_TIMEOUT = 12_000;

async function send(path: string, options: RequestOptions, deadline: AbortSignal): Promise<Response> {
  const headers = new Headers();
  if (options.body !== undefined) headers.set('content-type', 'application/json');

  const token = tokenStore.getAccessToken();
  if (!options.anonymous && token) headers.set('authorization', `Bearer ${token}`);
  for (const [name, value] of Object.entries(options.headers ?? {})) headers.set(name, value);

  /*
   * The caller's own signal still cancels, but the call's deadline is always
   * armed underneath it. `AbortSignal.any` combines them where it exists; the
   * manual path is the fallback.
   */
  const signal = options.signal
    ? (AbortSignal.any?.([options.signal, deadline]) ?? options.signal)
    : deadline;

  try {
    const response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal,
    });
    /*
     * Anything that came back — even a 500 — proves the line is up. Learning it
     * here rather than waiting for the next probe is what makes coming back
     * online feel immediate.
     */
    reportReachable();
    return response;
  } catch (error) {
    /*
     * A caller who cancelled on purpose gets their own abort back; anything
     * else that stopped the request from completing is the line being down, and
     * is reported as such so the till can queue rather than lose the bill.
     */
    if (options.signal?.aborted) throw error;
    /*
     * THE reason the till can queue instantly.
     *
     * Every request in the app — a background refetch of the day book, the
     * open-now poll, anything — reports a dead line the moment it finds one. So
     * by the time someone presses Save, the app almost always already knows,
     * and the bill goes straight to local storage with nothing to wait for.
     * Without this the first bill after a dropout paid the full timeout.
     */
    reportUnreachable();
    throw new NetworkError(
      deadline.aborted ? 'The server did not answer in time' : 'Could not reach the server',
    );
  }
}

/**
 * Returns the raw response envelope. Every backend route answers with either
 * `{ data }`, `{ data, page }`, or 204 with no body at all.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  /*
   * ONE deadline for the whole call, armed here rather than inside `send`.
   *
   * A 401 turns one call into three round trips — the request, the refresh, the
   * retry — and arming a fresh timer for each meant a caller who asked to wait
   * two and a half seconds could be held for three times that. The till asks
   * for 2.5s because it has a customer waiting; that budget has to mean the
   * whole call.
   */
  const deadline = AbortSignal.timeout(options.timeoutMs ?? REQUEST_TIMEOUT);

  let response = await send(path, options, deadline);

  if (response.status === 401 && !options.anonymous) {
    /*
     * Raced against the deadline rather than simply awaited. The refresh is
     * shared between every caller that hit a 401 at once, so it cannot carry
     * any one caller's budget — but no caller should be made to wait past their
     * own because they arrived second.
     */
    log.net(`401 on ${path} — refreshing session`);
    const outcome = await Promise.race([
      refreshSession(),
      whenAborted(deadline).then((): RefreshOutcome => 'unreachable'),
    ]);
    log.net(`session refresh: ${outcome}`);

    /*
     * Nobody answered. This is weather, not a refusal, so it is reported as a
     * dead line — which is what lets the till queue the bill instead of losing
     * it. Answering with the original 401 here would have bounced a pharmacist
     * to the sign-in screen mid-sale because their broadband dropped.
     */
    if (outcome === 'unreachable') {
      reportUnreachable();
      throw new NetworkError('The server did not answer in time');
    }
    if (outcome === 'refreshed') response = await send(path, options, deadline);
  }

  if (!response.ok) throw await toApiError(response);

  // 204 No Content — logout, role revoke, listing retire.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/** For the `{ data: T }` envelope, which is most routes. */
export async function requestData<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const payload = await request<{ data: T }>(path, options);
  return payload.data;
}

/** For the `{ data: T[], page }` envelope produced by `toPage`. */
export function requestPage<T>(path: string, options: RequestOptions = {}): Promise<Page<T>> {
  return request<Page<T>>(path, options);
}

export const emptyPage = <T,>(limit = 20): Page<T> => ({
  data: [],
  page: { limit, offset: 0, count: 0, hasMore: false },
});
